import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { RouterState, RouterStateModel } from '@ngxs/router-plugin';
import {
  Footer,
  FooterSection,
  HeaderBigImageLink,
  HeaderContent,
  NotificationBanner,
  Show,
  SocialLink,
} from 'www-cms';
import { SsrCookieService } from 'ngx-cookie-service-ssr';

interface HeaderInfo {
  shows: { lancaster: Show[]; branson: Show[] };
  bigImageLinks: HeaderBigImageLink[];
  headerContent: HeaderContent;
  footerContent: Footer;
  showTalkDesk: boolean;
  socialLinks?: SocialLink[];
  notificationBanners?: NotificationBanner[];
}

export namespace HeaderActions {
  export class SetShellInfo {
    static readonly type = '[Shell Resolver] Set header info';
    constructor(public payload: HeaderInfo) {}
  }

  export class SetPromoBannerVisibility {
    static readonly type = '[Shell Resolver] Set promo banner visibility';
    constructor(public payload: boolean) {}
  }

  export class SetMenu {
    static readonly type = '[Header] Set Menu';
    constructor(public payload: ShellStateModel['openedMenu']) {}
  }

  export class SetMobileMenu {
    static readonly type = '[Header] Set Mobile Menu';
    constructor(public payload: boolean) {}
  }

  export class SetOpenedMobileMenu {
    static readonly type = '[Mobile Menu] Set Opened Mobile Menu';
    constructor(public payload: ShellStateModel['openedMobileMenu']) {}
  }

  export class ShowLinkClicked {
    static readonly type = '[Theater Show List] Show Link Clicked';
  }

  export class PromoLinkClicked {
    static readonly type = '[Mobile Header] Promo Link Clicked';
  }

  export class BigImageLinkClicked {
    static readonly type = '[Big Image Link] Big Image Link Clicked';
  }
}

export interface ShellStateModel {
  mobileMenuOpened: boolean;
  openedMenu: 'liveShows' | 'none' | 'more';
  openedMobileMenu: 'liveShows' | 'more' | 'main';
  shows?: {
    lancaster: Show[];
    branson: Show[];
  };
  headerContent?: HeaderContent;
  bigImageLinks?: HeaderBigImageLink[];
  footerContent?: Footer;
  showTalkDesk?: boolean;
  socialLinks?: SocialLink[];
  notificationBanners?: NotificationBanner[];
}

const defaultHeaderState: ShellStateModel = {
  mobileMenuOpened: false,
  openedMenu: 'none',
  openedMobileMenu: 'main',
};

export class SetDismissPromoBanner {
  static readonly type = '[UserPref] Set Dismiss Promo Banner';
}

export class SetMediaToolkitSubscribedEmail {
  static readonly type = '[UserPref] Set Media Toolkit Subscribed Email';
  constructor(public payload: string) {}
}

export class SetPromoBannerVisibility {
  static readonly type = '[Shell Resolver] Set promo banner visibility';
  constructor(public payload: boolean) {}
}

export class DismissNotificationBanner {
  static readonly type = '[Notification Banner] Dismiss notification banner';
  constructor(public payload: number) {}
}

export class SetHasSeenPinchOverlay {
  static readonly type = '[UserPref] Set Has Seen Pinch Overlay';
}

interface UserPrefModel {
  dateUpdated: number;
  displayPromoBanner?: boolean;
  mediaToolkitSubscribedEmail?: string;
  dismissedNotificationBanners?: number[];
  hasSeenPinchOverlay?: boolean;
}

@State<UserPrefModel>({
  name: 'userPreferences',
  defaults: {
    dateUpdated: 0,
    displayPromoBanner: false,
  },
})
@Injectable()
export class UserPrefState {
  private readonly cookieService = inject(SsrCookieService);
  // Using a selector gives us type safety
  @Selector()
  static dateUpdated(state: UserPrefModel) {
    return state.dateUpdated;
  }

  @Selector()
  static mediaToolkitSubscribedEmail(state: UserPrefModel) {
    return state.mediaToolkitSubscribedEmail;
  }

  @Selector()
  static isPromoBannerVisible(state: UserPrefModel) {
    return state.displayPromoBanner;
  }

  @Selector()
  static hasSeenPinchOverlay(state: UserPrefModel) {
    return state.hasSeenPinchOverlay;
  }

  @Action(SetDismissPromoBanner)
  setDismissPromoBanner(ctx: any) {
    ctx.patchState({
      // Set the date to now.
      dateUpdated: Date.now(),
      displayPromoBanner: false,
    });
  }

  @Action(SetMediaToolkitSubscribedEmail)
  setMediaToolkitSubscribedEmail(
    ctx: any,
    action: SetMediaToolkitSubscribedEmail
  ) {
    ctx.patchState({
      // Set the date to now.
      mediaToolkitSubscribedEmail: action.payload,
    });
  }

  @Action(SetPromoBannerVisibility)
  setPromoBannerVisibility(
    ctx: StateContext<UserPrefModel>,
    action: SetPromoBannerVisibility
  ) {
    if (action.payload === ctx.getState().displayPromoBanner) return;
    return ctx.patchState({
      displayPromoBanner: action.payload,
    });
  }

  @Action(DismissNotificationBanner)
  dismissNotificationBanner(
    ctx: StateContext<UserPrefModel>,
    action: DismissNotificationBanner
  ) {
    const dismissedNotificationBanners =
      ctx.getState().dismissedNotificationBanners || [];
    if (dismissedNotificationBanners.includes(action.payload)) return;

    this.cookieService.set(
      'dismissedNotificationBanners',
      JSON.stringify([...dismissedNotificationBanners, action.payload])
    );

    return ctx.patchState({
      dismissedNotificationBanners: [
        ...dismissedNotificationBanners,
        action.payload,
      ],
    });
  }

  @Action(SetHasSeenPinchOverlay)
  setHasSeenPinchOverlay(ctx: StateContext<UserPrefModel>) {
    return ctx.patchState({
      hasSeenPinchOverlay: true,
    });
  }
}

@State<ShellStateModel>({
  name: 'shellState',
  defaults: defaultHeaderState,
})
@Injectable()
export class ShellState {
  @Selector()
  static shellState(state: ShellStateModel) {
    return state;
  }

  @Selector()
  static headerContent(state: ShellStateModel) {
    return state.headerContent;
  }

  @Selector()
  static openedMobileMenu(state: ShellStateModel) {
    return state.openedMobileMenu;
  }

  @Selector()
  static shows(state: ShellStateModel) {
    return state.shows;
  }

  @Selector()
  static bigImageLinks(state: ShellStateModel) {
    return state.bigImageLinks;
  }

  @Selector()
  static isMenuOrMobileMenuOpened(state: ShellStateModel) {
    return state.openedMenu !== 'none' || state.mobileMenuOpened;
  }

  @Selector()
  static isMobileMenuOpened(state: ShellStateModel) {
    return state.mobileMenuOpened;
  }

  @Selector()
  static footerContent(state: ShellStateModel) {
    return state.footerContent;
  }

  @Selector()
  static showTalkDesk(state: ShellStateModel) {
    return state.showTalkDesk;
  }

  @Selector()
  static socialLinks(state: ShellStateModel) {
    return state.socialLinks;
  }

  @Selector()
  static mappedFooterContentSections(state: ShellStateModel) {
    return state.footerContent?.footerSections.reduce(
      (acc, section) => {
        acc[section.identifier] = section;
        return acc;
      },
      {} as Record<string, FooterSection>
    );
  }

  @Selector()
  static getNotificationBanners(state: ShellStateModel) {
    return state.notificationBanners;
  }

  @Selector([ShellState, UserPrefState, RouterState])
  static getNotificationBannerForRoute(
    state: ShellStateModel,
    userPreferences: UserPrefModel,
    routerState: RouterStateModel
  ) {
    return state.notificationBanners
      ?.filter(
        (banner) =>
          !userPreferences.dismissedNotificationBanners?.includes(banner.id)
      )
      .find((banner) => {
        const testUrl = banner.urlToShowOn.replace(/\*/g, '[^/]*') + '$';
        return new RegExp(testUrl).test(routerState.state?.url || '');
      });
  }

  @Action(HeaderActions.SetShellInfo)
  setShellInfo(ctx: any, action: HeaderActions.SetShellInfo) {
    ctx.patchState(action.payload);
  }

  @Action(HeaderActions.SetMenu)
  openMenu(ctx: any, action: HeaderActions.SetMenu) {
    ctx.patchState({ openedMenu: action.payload });
  }

  @Action(HeaderActions.SetMobileMenu)
  setMobileMenu(ctx: any, action: HeaderActions.SetMobileMenu) {
    ctx.patchState({ mobileMenuOpened: action.payload });
  }

  @Action(HeaderActions.SetOpenedMobileMenu)
  setOpenedMobileMenu(ctx: any, action: HeaderActions.SetOpenedMobileMenu) {
    ctx.patchState({ openedMobileMenu: action.payload });
  }

  @Action(HeaderActions.SetPromoBannerVisibility)
  setPromoBannerVisibility(
    ctx: StateContext<ShellStateModel>,
    action: HeaderActions.SetPromoBannerVisibility
  ) {
    ctx.patchState({
      headerContent: {
        ...ctx.getState().headerContent!,
        showPromoBanner: action.payload,
      },
    });
  }

  @Action([
    HeaderActions.ShowLinkClicked,
    HeaderActions.BigImageLinkClicked,
    HeaderActions.PromoLinkClicked,
  ])
  showLinkClicked(ctx: any) {
    ctx.dispatch(new HeaderActions.SetMobileMenu(false));
    ctx.dispatch(new HeaderActions.SetOpenedMobileMenu('main'));
    ctx.dispatch(new HeaderActions.SetMenu('none'));
  }
}
